
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface MetaInsightsSummary {
  spend: number;
  clicks: number;
  leads: number;
  cpc: number;
  cpl: number;
  ctr: number;
  last_data: string | null;
}

export interface MetaInsightsTimeData {
  date_start: string;
  spend: number;
  clicks: number;
  leads: number;
}

export interface MetaInsightsTopAds {
  campaign_name: string;
  campaign_id: string;
  spend: number;
  clicks: number;
  leads: number;
  cpc: number;
  cpl: number;
  ctr: number;
}

export const useMetaInsights = (isConnected: boolean) => {
  const [summary, setSummary] = useState<MetaInsightsSummary | null>(null);
  const [timeData, setTimeData] = useState<MetaInsightsTimeData[]>([]);
  const [topAds, setTopAds] = useState<MetaInsightsTopAds[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const { user } = useAuth();

  // Debounced fetch to prevent multiple requests
  const debouncedFetch = useCallback((fn: Function) => {
    const now = Date.now();
    if (now - lastFetchTime < 5000) { // 5 seconds debounce
      console.log("Debouncing API request");
      return;
    }
    
    setLastFetchTime(now);
    fn();
  }, [lastFetchTime]);

  // Fetch summary data
  const fetchSummaryData = useCallback(() => {
    if (!user?.id || !isConnected) return;
    
    debouncedFetch(async () => {
      try {
        console.log("Fetching Meta insights summary");
        setIsLoading(true);
        setError(null);
        
        // Using the raw REST API approach to avoid type issues
        const response = await fetch(`https://brripffladqztihvuxts.supabase.co/rest/v1/meta_insights_view?customer_id=eq.${user.id}&select=*`, {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJycmlwZmZsYWRxenRpaHZ1eHRzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDExNjgwNDQsImV4cCI6MjA1Njc0NDA0NH0.UhMgZ036bp1lfPKAnEcAd2QVXj8LQVCOutOVhd_LII0',
            'Authorization': `Bearer ${user.id}`
          }
        });
        
        if (!response.ok) {
          console.error('Failed to fetch Meta insights summary:', response.statusText);
          setError('Fehler beim Laden der Meta-Daten');
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log("Meta insights summary data:", data);
        
        if (data && data.length > 0) {
          setSummary({
            spend: data[0].spend || 0,
            clicks: data[0].clicks || 0,
            leads: data[0].leads || 0,
            cpc: data[0].cpc || 0,
            cpl: data[0].cpl || 0,
            ctr: data[0].ctr || 0,
            last_data: data[0].last_data
          });
          
          // After getting summary, fetch time data and top ads
          fetchTimeData();
          fetchTopAds();
        } else {
          console.log("No Meta insights summary data found");
          setError('Keine Meta-Daten vorhanden. Bitte synchronisieren Sie Ihre Daten.');
        }
      } catch (error) {
        console.error('Error fetching Meta insights summary:', error);
        setError('Fehler beim Laden der Meta-Daten');
      } finally {
        setIsLoading(false);
      }
    });
  }, [user?.id, isConnected, debouncedFetch]);

  // Fetch time-based data for charts
  const fetchTimeData = useCallback(async (period: 'week' | 'month' = 'week') => {
    if (!user?.id || !isConnected) return;
    
    try {
      console.log("Fetching Meta time data");
      
      // Calculate date range
      const today = new Date();
      let startDate = new Date();
      
      if (period === 'week') {
        startDate.setDate(today.getDate() - 7);
      } else {
        startDate.setMonth(today.getMonth() - 1);
      }
      
      const formattedStartDate = startDate.toISOString().split('T')[0];
      
      // Using the raw REST API approach
      const response = await fetch(`https://brripffladqztihvuxts.supabase.co/rest/v1/meta_insights?customer_id=eq.${user.id}&date_start=gte.${formattedStartDate}&select=date_start,spend,clicks,leads&order=date_start.asc`, {
        headers: {
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJycmlwZmZsYWRxenRpaHZ1eHRzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDExNjgwNDQsImV4cCI6MjA1Njc0NDA0NH0.UhMgZ036bp1lfPKAnEcAd2QVXj8LQVCOutOVhd_LII0',
          'Authorization': `Bearer ${user.id}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch Meta insights time data:', response.statusText);
        return;
      }
      
      const data = await response.json();
      console.log("Meta time data:", data);
      
      if (data && data.length > 0) {
        const typedData: MetaInsightsTimeData[] = data.map((item: any) => ({
          date_start: item.date_start,
          spend: item.spend || 0,
          clicks: item.clicks || 0,
          leads: item.leads || 0
        }));
        setTimeData(typedData);
      } else {
        console.log("No time data available");
        // Generate empty time data for the last 7 days
        const emptyTimeData: MetaInsightsTimeData[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          emptyTimeData.push({
            date_start: date.toISOString().split('T')[0],
            spend: 0,
            clicks: 0,
            leads: 0
          });
        }
        setTimeData(emptyTimeData);
      }
    } catch (error) {
      console.error('Error fetching Meta insights time data:', error);
    }
  }, [user?.id, isConnected]);

  // Fetch top performing ads/campaigns
  const fetchTopAds = useCallback(async (limit: number = 5) => {
    if (!user?.id || !isConnected) return;
    
    try {
      console.log("Fetching top ads");
      
      // Using the raw REST API approach
      const response = await fetch(`https://brripffladqztihvuxts.supabase.co/rest/v1/meta_insights?customer_id=eq.${user.id}&select=campaign_name,campaign_id,spend,clicks,leads,cpc,cpl,ctr&order=leads.desc.nullslast&limit=${limit}`, {
        headers: {
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJycmlwZmZsYWRxenRpaHZ1eHRzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDExNjgwNDQsImV4cCI6MjA1Njc0NDA0NH0.UhMgZ036bp1lfPKAnEcAd2QVXj8LQVCOutOVhd_LII0',
          'Authorization': `Bearer ${user.id}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch top ads:', response.statusText);
        return;
      }
      
      const data = await response.json();
      console.log("Top ads data:", data);
      
      if (data && data.length > 0) {
        const typedData: MetaInsightsTopAds[] = data.map((item: any) => ({
          campaign_name: item.campaign_name || '',
          campaign_id: item.campaign_id || '',
          spend: item.spend || 0,
          clicks: item.clicks || 0,
          leads: item.leads || 0,
          cpc: item.cpc || 0,
          cpl: item.cpl || 0,
          ctr: item.ctr || 0
        }));
        setTopAds(typedData);
      }
    } catch (error) {
      console.error('Error fetching Meta insights top ads:', error);
    }
  }, [user?.id, isConnected]);

  // Initialize data fetching when component mounts
  useEffect(() => {
    if (isConnected) {
      fetchSummaryData();
    }
  }, [isConnected, fetchSummaryData]);

  // Provide a refresh method for manual refresh
  const refreshSummary = useCallback(() => {
    if (isConnected) {
      fetchSummaryData();
    }
  }, [isConnected, fetchSummaryData]);

  return {
    summary,
    timeData,
    topAds,
    isLoading,
    error,
    refreshSummary
  };
};
